<template>
  <BreadCrumb PageTitle="Poste d'emballage" />

  <!-- Barre du bon -->
  <div class="card mb-20 border-0 rounded-0 bg-white letter-spacing">
    <div class="card-body p-15 p-md-20 d-flex align-items-center justify-content-between flex-wrap gap-3">
      <div class="d-flex align-items-center gap-4 flex-wrap">
        <div>
          <span class="text-muted me-2">Bon</span>
          <b class="fs-20 pk-accent">{{ refNote }}</b>
        </div>
        <div v-if="state">
          <span class="text-muted me-2">Colis</span>
          <b class="fs-20 pk-accent">{{ state.done ? state.total : state.index }} / {{ state.total }}</b>
        </div>
      </div>
      <button type="button" class="btn btn-outline-secondary" @click="pause">
        <i class="ph ph-pause me-1"></i>Pause
      </button>
    </div>
  </div>

  <div v-if="!state" class="text-center py-50 text-muted">Chargement...</div>

  <!-- Tous les colis sont emballés -->
  <div v-else-if="state.done" class="card border-0 rounded-0 bg-white letter-spacing">
    <div class="card-body p-30 text-center">
      <i class="ph ph-check-circle pk-ok" style="font-size: 72px"></i>
      <h3 class="mt-10">Tous les colis sont emballés</h3>
      <p class="text-muted">{{ state.total }} colis prêts pour le bon {{ refNote }}.</p>
      <div class="d-flex justify-content-center gap-2 flex-wrap">
        <button v-if="!state.note.saved" type="button" class="btn pk-btn" :disabled="saving" @click="saveNote">
          <i class="ph ph-floppy-disk me-1"></i>Enregistrer le bon
        </button>
        <router-link class="btn btn-outline-secondary" to="/packing">Retour à la liste</router-link>
      </div>
    </div>
  </div>

  <template v-else>
    <!-- Étape 3 : coller puis scanner l'étiquette -->
    <div v-if="labelStep" class="row g-3">
      <div class="col-12">
        <div class="alert pk-alert-ok mb-0">
          <i class="ph ph-check-circle me-2"></i>
          Tous les produits sont vérifiés — {{ state.parcel.scanned_qty }} / {{ state.parcel.total_qty }} pièces
        </div>
      </div>
      <div class="col-lg-5">
        <div class="card border rounded-0 bg-white h-100">
          <div class="card-body">
            <h5 class="mb-15">Étiquette imprimée</h5>
            <div class="border rounded-3 p-15 mb-15">
              <div class="fs-24 fw-bold text-center">{{ state.parcel.code }}</div>
              <hr />
              <div class="fs-13 text-muted">Destinataire</div>
              <div class="fw-bold">{{ state.parcel.receiver }}</div>
              <div>{{ state.parcel.phone }}</div>
              <div class="d-flex justify-content-between mt-10">
                <b>{{ state.parcel.city_name }}</b>
                <b>{{ state.parcel.price }} {{ $devis }}</b>
              </div>
              <div class="text-muted fs-13">{{ state.parcel.address }}</div>
            </div>
            <button type="button" class="btn btn-outline-primary w-100" @click="printLabel">
              <i class="ph ph-printer me-1"></i>Réimprimer la même étiquette
            </button>
          </div>
        </div>
      </div>
      <div class="col-lg-7">
        <div class="card pk-focus rounded-0 bg-white h-100">
          <div class="card-body">
            <h4 class="mb-15"><i class="ph ph-scan me-2 pk-accent"></i>Scannez l'étiquette collée sur le carton...</h4>
            <input
              ref="labelInput"
              v-model="scan"
              type="text"
              class="form-control form-control-lg pk-scan"
              placeholder="En attente du scan..."
              autocomplete="off"
              @keyup.enter="submitLabel"
              @blur="refocus"
            />
            <div v-if="message" class="mt-15 alert" :class="messageOk ? 'pk-alert-ok' : 'alert-danger'">{{ message }}</div>
            <ol class="mt-20 ps-20 text-muted">
              <li>Collez l'étiquette sur le carton</li>
              <li>Scannez le code du colis</li>
              <li>Le colis suivant s'affiche automatiquement</li>
            </ol>
            <div class="fs-13 text-muted">Seule l'étiquette du colis <b>{{ state.parcel.code }}</b> est acceptée.</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Étape 2 : scanner les produits -->
    <div v-else class="row g-3">
      <div class="col-lg-8">
        <div class="card border-0 rounded-0 bg-white h-100">
          <div class="card-body p-20">
            <h3 class="mb-15">Scanner les produits</h3>
            <div v-if="state.parcel.problems && state.parcel.problems.length" class="alert alert-danger py-2 fs-13">
              <i class="ph ph-warning me-1"></i><b>Problème signalé sur ce colis :</b>
              <span v-for="pr in state.parcel.problems" :key="pr.id" class="d-block">
                {{ pr.reason }} <span class="text-muted">({{ pr.reported_by_name }})</span>
              </span>
            </div>
            <div class="row mb-15 fs-14">
              <div class="col-md-4"><span class="text-muted d-block">Colis actuel</span><b class="pk-accent">{{ state.parcel.code }}</b></div>
              <div class="col-md-4"><span class="text-muted d-block">Client</span><b>{{ state.parcel.customer_name }}</b></div>
              <div class="col-md-4"><span class="text-muted d-block">Destination</span><b>{{ state.parcel.city_name }}</b></div>
            </div>
            <input
              ref="productInput"
              v-model="scan"
              type="text"
              class="form-control form-control-lg pk-scan mb-10"
              placeholder="Scannez le code-barres du produit..."
              autocomplete="off"
              :disabled="state.parcel.verified"
              @keyup.enter="submitProduct"
              @blur="refocus"
            />
            <div v-if="message" class="mb-10 alert py-2" :class="messageOk ? 'pk-alert-ok' : 'alert-danger'">{{ message }}</div>

            <table class="table align-middle">
              <thead>
                <tr><th>#</th><th>Produit</th><th>Référence</th><th>Emplacement</th><th>Quantité scannée</th><th>État</th></tr>
              </thead>
              <tbody>
                <tr v-for="(it, i) in state.parcel.items" :key="it.id" :class="{ 'pk-row-todo': it.scanned_qty < it.qnty }">
                  <td>{{ i + 1 }}</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <img :src="it.product_pic || noImage" width="56" height="56" class="rounded-2 border me-3 pk-thumb" :alt="it.product_name" loading="lazy" />
                      <div>
                        <b>{{ it.product_name }}</b>
                        <div class="text-muted fs-12">{{ it.variant_name }}</div>
                      </div>
                    </div>
                  </td>
                  <td>{{ it.reference }}</td>
                  <td>{{ it.location || "—" }}</td>
                  <td><b>{{ it.scanned_qty }}</b> sur {{ it.qnty }}</td>
                  <td>
                    <span v-if="it.scanned_qty >= it.qnty" class="badge pk-badge-ok">Vérifié</span>
                    <span v-else class="badge pk-badge-todo">À compléter</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="col-lg-4">
        <div class="card border-0 rounded-0 bg-white h-100">
          <div class="card-body p-20">
            <h5>Progression du colis</h5>
            <div class="mb-10">
              <span class="fs-36 fw-bold pk-accent">{{ state.parcel.scanned_qty }}</span>
              <span class="fs-20"> / {{ state.parcel.total_qty }}</span>
              <span class="fs-18 ms-1">pièces scannées</span>
            </div>
            <div class="progress mb-10" style="height: 10px">
              <div class="progress-bar pk-bar" :style="{ width: parcelPercent + '%' }"></div>
            </div>
            <div v-if="state.parcel.remaining.length" class="mb-15">
              Il reste
              <b class="text-danger">{{ remainingQty }} pièce{{ remainingQty > 1 ? "s" : "" }}</b> :
              {{ state.parcel.remaining.map((r) => r.reference + (r.qty > 1 ? " ×" + r.qty : "")).join(", ") }}
            </div>
            <button type="button" class="btn pk-btn w-100 mb-5" :disabled="!state.parcel.verified" @click="printLabelAndNext">
              <i class="ph ph-printer me-1"></i>Imprimer l'étiquette
            </button>
            <div v-if="!state.parcel.verified" class="text-muted fs-13 mb-15">Disponible lorsque tous les produits sont vérifiés.</div>
            <button type="button" class="btn btn-outline-primary w-100 mb-10" @click="problem">
              <i class="ph ph-warning me-1"></i>Signaler un problème
            </button>
            <button type="button" class="btn btn-link text-muted w-100" :disabled="state.parcel.scanned_qty === 0" @click="reset">
              Recommencer ce colis
            </button>
            <div class="alert alert-info fs-13 mt-10 mb-0">Produit incorrect ou quantité dépassée : scan refusé.</div>
          </div>
        </div>
      </div>
    </div>
  </template>
  <!-- Signaler un problème -->
  <div class="modal fade" id="modal_packing_problem" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content p-15 p-md-30">
        <div class="modal-header border-0 justify-content-center">
          <h1 class="modal-title fs-4">Signaler un problème</h1>
        </div>
        <div class="modal-body">
          <v-select
            v-model="problemReason"
            :options="problemReasons"
            :clearable="false"
            :searchable="false"
            placeholder="Choisir un motif"
            class="custom-select-height"
          />
        </div>
        <div class="modal-footer border-0 justify-content-center">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Annuler</button>
          <button type="button" class="btn pk-btn" :disabled="!problemReason || problemSaving" @click="submitProblem">
            <span v-if="problemSaving" class="spinner-border spinner-border-sm me-1"></span>Signaler
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2";
import { Modal } from "bootstrap";
import api from "@/utils/axios";
import BreadCrumb from "@/components/Common/BreadCrumb.vue";
import noImage from "@/assets/images/NoImage.png";

const ERRORS: Record<string, string> = {
  wrong_product: "Produit incorrect pour ce colis : scan refusé.",
  qty_exceeded: "Quantité dépassée pour ce produit : scan refusé.",
  incomplete: "Tous les produits doivent être vérifiés avant de fermer le colis.",
  wrong_label: "Mauvaise étiquette : ce n'est pas celle de ce colis.",
  not_available: "Ce colis n'est plus disponible pour l'emballage.",
};

export default defineComponent({
  name: "PackingStationPage",
  components: { BreadCrumb },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const id = String(route.params.id);
    const refNote = String(route.params.refNote);

    const state = ref<any>(null);
    const scan = ref("");
    const message = ref("");
    const messageOk = ref(false);
    const labelStep = ref(false);
    const saving = ref(false);
    const busy = ref(false);
    const productInput = ref<HTMLInputElement | null>(null);
    const labelInput = ref<HTMLInputElement | null>(null);

    const parcelPercent = computed(() =>
      state.value?.parcel ? Math.round((state.value.parcel.scanned_qty / Math.max(1, state.value.parcel.total_qty)) * 100) : 0
    );
    const remainingQty = computed(() =>
      (state.value?.parcel?.remaining || []).reduce((s: number, r: any) => s + r.qty, 0)
    );

    function play(ok: boolean) {
      try {
        new Audio(ok ? "/sound.mp3" : "/erro.mp3").play();
      } catch (e) {
        /* sound is optional */
      }
    }
    function say(text: string, ok: boolean) {
      message.value = text;
      messageOk.value = ok;
      play(ok);
    }
    function focusInput() {
      nextTick(() => (labelStep.value ? labelInput.value : productInput.value)?.focus());
    }
    function refocus() {
      // the scanner types in the focused field: keep the focus on it
      setTimeout(() => {
        if (!Swal.isVisible()) focusInput();
      }, 50);
    }

    async function load() {
      const res = await api.get(`/api/auth/packing/notes/${id}/current`);
      state.value = res.data;
      labelStep.value = false;
      focusInput();
    }

    async function submitProduct() {
      const code = scan.value.trim();
      scan.value = "";
      if (!code || busy.value || !state.value?.parcel) return;
      busy.value = true;
      try {
        const res = await api.post("/api/auth/packing/scan-product", { parcel_id: state.value.parcel.id, code });
        if (res.data.message === "success") {
          state.value = res.data.state;
          say(`${code} : vérifié`, true);
        } else {
          say(ERRORS[res.data.message] || "Scan refusé.", false);
        }
      } catch (e) {
        say("Erreur, merci de réessayer.", false);
      } finally {
        busy.value = false;
        focusInput();
      }
    }

    async function submitLabel() {
      const code = scan.value.trim();
      scan.value = "";
      if (!code || busy.value || !state.value?.parcel) return;
      busy.value = true;
      try {
        const res = await api.post("/api/auth/packing/scan-label", { parcel_id: state.value.parcel.id, code });
        if (res.data.message === "success") {
          state.value = res.data.state;
          labelStep.value = false;
          say(`Colis ${code} emballé`, true);
        } else {
          say(ERRORS[res.data.message] || "Scan refusé.", false);
        }
      } catch (e) {
        say("Erreur, merci de réessayer.", false);
      } finally {
        busy.value = false;
        focusInput();
      }
    }

    async function printLabel() {
      try {
        const res = await api.get(`/api/auth/packing/parcels/${state.value.parcel.id}/label`, { responseType: "arraybuffer" });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        window.open(url, "_blank");
      } catch (e) {
        Swal.fire({ icon: "error", title: "Oops...", text: "Impossible de générer l'étiquette" });
      }
    }
    async function printLabelAndNext() {
      await printLabel();
      message.value = "";
      labelStep.value = true;
      focusInput();
    }

    async function reset() {
      const r = await Swal.fire({ text: "Recommencer le scan de ce colis ?", icon: "question", showCancelButton: true, confirmButtonText: "Oui", cancelButtonText: "Annuler" });
      if (!r.isConfirmed) return;
      const res = await api.post("/api/auth/packing/reset-parcel", { parcel_id: state.value.parcel.id });
      if (res.data.message === "success") state.value = res.data.state;
      message.value = "";
      focusInput();
    }

    // reasons stay in French (stored as is); the screen translates the labels
    const problemReasons = ["Produit manquant", "Produit endommagé"];
    const problemReason = ref<string | null>(null);
    const problemSaving = ref(false);

    function problem() {
      problemReason.value = null;
      const el = document.getElementById("modal_packing_problem");
      if (el) (Modal.getInstance(el) || new Modal(el)).show();
    }
    async function submitProblem() {
      if (!problemReason.value) return;
      problemSaving.value = true;
      try {
        const res = await api.post("/api/auth/packing/problem", { parcel_id: state.value.parcel.id, reason: problemReason.value });
        if (res.data.state) state.value = res.data.state;
        const el = document.getElementById("modal_packing_problem");
        if (el) Modal.getInstance(el)?.hide();
        Swal.fire({ position: "top-end", icon: "success", title: "Problème signalé", showConfirmButton: false, timer: 1500 });
      } finally {
        problemSaving.value = false;
        focusInput();
      }
    }

    async function saveNote() {
      saving.value = true;
      try {
        const res = await api.post(`/api/auth/save_bls/${id}`);
        if (res.data.message === "success") {
          Swal.fire({ position: "top-end", icon: "success", title: "Le bon de livraison est bien enregistré", showConfirmButton: false, timer: 1500 });
          router.push("/packing");
        } else if (res.data.message === "not_packed") {
          Swal.fire({ icon: "error", text: `Tous les colis doivent être emballés (${res.data.packed} / ${res.data.total}).` });
        } else {
          Swal.fire({ icon: "error", title: "Oops...", text: "Erreur, merci de réessayer encore!" });
        }
      } catch (e) {
        Swal.fire({ icon: "error", title: "Oops...", text: "Erreur, merci de réessayer encore!" });
      } finally {
        saving.value = false;
      }
    }

    const pause = () => router.push("/packing");

    onMounted(load);

    return {
      refNote, state, scan, message, messageOk, labelStep, saving, productInput, labelInput,
      parcelPercent, remainingQty, submitProduct, submitLabel, printLabel, printLabelAndNext,
      reset, problem, saveNote, pause, refocus, noImage,
      problemReasons, problemReason, problemSaving, submitProblem,
    };
  },
});
</script>

<style scoped>
.pk-accent { color: var(--ui-accent); }
.pk-thumb { object-fit: cover; background: #fff; flex: none; }
.pk-ok { color: #198754; }
.pk-btn { background: var(--ui-accent); color: #fff !important; border: 0; font-weight: 600; white-space: nowrap; }
.pk-btn:hover:not(:disabled) { background: var(--ui-accent-strong); color: #fff; }
.pk-btn:disabled { background: #d9dde3; color: #7a828c; }
.pk-bar { background: var(--ui-accent); }
.pk-focus { border: 2px solid var(--ui-accent); }
.pk-scan { border: 2px solid var(--ui-accent); }
.pk-row-todo { background: rgba(var(--ui-accent-rgb), 0.08); }
.pk-alert-ok { background: #e8f5ec; color: #146c43; border: 1px solid #b7dfc4; }
.pk-badge-ok { background: #e8f5ec; color: #146c43; }
.pk-badge-todo { background: rgba(var(--ui-accent-rgb), 0.15); color: var(--ui-accent-text); }
</style>
